import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronDown } from 'lucide-react';

import { useContactMail } from './useContactMail';
import { ListCard, PageHeader, TransitionLink } from '../../components';

/**
 * 질문의 순서 — 문구는 `web.more.docs.faq.items.<id>.{q,a}` 에 있다. 항목을 더하면 여기와
 * 두 로케일에 같이 더한다(`FaqPage.spec` 이 양방향으로 짝을 확인한다).
 */
export const FAQ_IDS = [
  'frequency',
  'variance',
  'score',
  'mic',
  'storage',
  'delete',
] as const;

type FaqId = (typeof FAQ_IDS)[number];

/**
 * 자주 묻는 질문(Figma `MY #자주 묻는 질문`) — 한 카드 안의 아코디언.
 *
 * **한 번에 하나만 열린다.** 처음에는 첫 질문이 열려 있고(시안 상태), 다른 질문을 누르면
 * 그것으로 옮겨 가며, 열린 질문을 다시 누르면 닫힌다. 질문 줄은 `h2` 안의 버튼이다 —
 * 스크린리더의 제목 이동으로 질문 사이를 건너뛸 수 있고, `aria-expanded` 가 열림을 말한다.
 *
 * 답 안의 `support@muzly.io` 는 문의 화면의 버튼과 같은 동작의 링크다(`useContactMail`).
 * 카드 아래의 `1:1 문의하기` 는 문의 화면으로 간다 — 거기에 받는 주소와 메일 버튼이 있다.
 */
export const FaqPage = () => {
  const { t } = useTranslation();
  const { withMailLinks } = useContactMail();
  const baseId = useId();
  const [open, setOpen] = useState<FaqId | null>(FAQ_IDS[0]);

  return (
    <div className="flex min-h-dvh flex-col bg-background pb-10">
      <PageHeader title={t('web.more.docs.faq.title')} />

      <div className="flex flex-col gap-6 px-[18px] pt-8">
        <ListCard>
          {FAQ_IDS.map((id) => {
            const expanded = open === id;
            const panelId = `${baseId}-${id}`;
            return (
              <div key={id} className="flex flex-col py-[18px]">
                <h2>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => setOpen(expanded ? null : id)}
                    // 그려진 줄은 21px, 누르는 자리만 위아래로 넓힌다(44px 이상).
                    className="relative flex w-full items-start gap-2 text-left text-[15px] leading-[1.4] after:absolute after:-inset-x-2 after:-inset-y-3 after:content-['']"
                  >
                    <span aria-hidden="true" className="font-bold text-primary">
                      Q
                    </span>
                    <span
                      className={cn(
                        'min-w-0 flex-1 break-keep tracking-[-0.075px] text-bk-900',
                        expanded && 'font-medium',
                      )}
                    >
                      {t(`web.more.docs.faq.items.${id}.q`)}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        'h-5 w-5 shrink-0 text-bk-800 transition-transform motion-reduce:transition-none',
                        expanded && 'rotate-180',
                      )}
                    />
                  </button>
                </h2>
                <div id={panelId} hidden={!expanded}>
                  <p className="mt-3.5 select-text whitespace-pre-line break-keep rounded-[14px] bg-bk-50 px-4 py-3.5 text-sm leading-[1.6] tracking-[-0.07px] text-bk-700">
                    {withMailLinks(t(`web.more.docs.faq.items.${id}.a`))}
                  </p>
                </div>
              </div>
            );
          })}
        </ListCard>

        <div className="flex flex-col items-center gap-3 pt-2">
          <p className="text-sm leading-[1.4] tracking-[-0.07px] text-bk-600">
            {t('web.more.docs.faq.notFound')}
          </p>
          <TransitionLink
            to="/more/docs/contact"
            className="flex min-h-11 items-center justify-center rounded-full border border-bk-200 bg-white px-[22px] text-[15px] font-medium leading-[1.4] tracking-[-0.075px] text-bk-900"
          >
            {t('web.more.docs.faq.contact')}
          </TransitionLink>
        </div>
      </div>
    </div>
  );
};
